import produce from "immer";
import type React from "react";

import { logger } from "@jslib/shared/utils/logger";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { Message, useMessage } from "@components/Message/Message";
import { FormFieldCheckbox } from "@components/FormFields/FormFieldCheckbox/FormFieldCheckbox";
import { FormFieldText } from "@components/FormFields/FormFieldText/FormFieldText";
import { FormFieldSelect } from "@components/FormFields/FormFieldSelect/FormFieldSelect";
import { DateComponent } from "@components/DateComponent/DateComponent";
import { FormFieldRadio } from "@components/FormFields/FormFieldRadio/FormFieldRadio";

import { type CustomFormFieldConfig, type VerificationService } from "@/lib/types/types";
import { type MessageId } from "@/lib/intl/types";
import { getFormFieldSelectOptions } from "@/lib/utils/structures/FormSelectChoice";

export const FormFieldConfigType = {
  TEXT: "text",
  CHECKBOX: "checkbox",
  SELECT: "select",
  RADIO: "radio",
  DATE: "date",
  DATEYM: "dateYm",
} as const;

export interface FormFieldCustomProps {
  config: CustomFormFieldConfig;
  verificationService: VerificationService;
}

export interface FormFieldBaseComponentProps<TFieldValue> {
  fieldId: string; // should be camelCase
  value: TFieldValue;
  onChange: (value: TFieldValue) => void;
  label: React.ReactNode; // Label is always required for screen readers
  errorMessage?: React.ReactNode;
}

export const getMessageIdForLabel = (config: CustomFormFieldConfig) =>
  `${config.fieldId}Label` as MessageId;
export const getMessageIdForExplanation = (config: CustomFormFieldConfig) =>
  `${config.fieldId}Explanation` as MessageId;
export const getMessageIdForPlaceholder = (config: CustomFormFieldConfig) =>
  `${config.fieldId}Placeholder` as MessageId;

/**
 * Custom form field component.
 * Implementors can render a form field passing in config using setOptions, rather than
 * having to create an entire React project and override component(s).
 */
export const FormFieldCustom = ({ config, verificationService }: FormFieldCustomProps) => {
  const { i18n, msg } = useMessage();
  const currentValue = getSafe(() => verificationService.viewModel.metadata[config.fieldId]);
  const errorId = getSafe(() => verificationService.fieldValidationErrors[config.fieldId]);

  const onChange = (value) => {
    verificationService.updateViewModel(
      produce(verificationService.viewModel, (draft) => {
        draft.metadata = draft.metadata || {};
        draft.metadata[config.fieldId] = value;
      }),
    );
    verificationService.updateFieldValidationErrors(
      produce(verificationService.fieldValidationErrors, (draft) => {
        const errorId: ReturnType<typeof config.validate> = config.validate(value);
        if (errorId === undefined) {
          delete draft[config.fieldId];
        }
        // Don't set error. Only do that when submit is clicked, same as jslib's built-in fields
      }),
    );
  };

  switch (config.fieldType) {
    case FormFieldConfigType.TEXT:
      return (
        <FormFieldText
          fieldId={config.fieldId}
          value={`${currentValue || ""}`}
          onChange={onChange}
          disabled={config.disabled}
          placeholder={msg({ id: getMessageIdForPlaceholder(config) })}
          label={<Message id={getMessageIdForLabel(config)} />}
          explanation={
            i18n.exists(getMessageIdForExplanation(config))
              ? msg({ id: getMessageIdForExplanation(config) })
              : undefined
          }
          errorMessage={errorId && <Message id={`errorId.${errorId}`} />}
          showPlaceholderAndHideLabel={config.showPlaceholderAndHideLabel}
          required={config.required}
        />
      );
    case FormFieldConfigType.CHECKBOX:
      return (
        <FormFieldCheckbox
          fieldId={config.fieldId}
          value={typeof currentValue === "boolean" ? currentValue : false}
          onChange={onChange}
          disabled={config.disabled}
          label={<Message id={getMessageIdForLabel(config)} />}
          errorMessage={errorId && <Message id={`errorId.${errorId}`} />}
        />
      );
    case FormFieldConfigType.SELECT:
      return (
        <FormFieldSelect
          fieldId={config.fieldId}
          value={`${currentValue || ""}`}
          onChange={onChange}
          options={getFormFieldSelectOptions(config.fieldId, config.options, msg)}
          disabled={config.disabled}
          placeholder={msg({ id: getMessageIdForPlaceholder(config) })}
          label={<Message id={getMessageIdForLabel(config)} />}
          errorMessage={errorId && <Message id={`errorId.${errorId}`} />}
          showPlaceholderAndHideLabel={config.showPlaceholderAndHideLabel}
          required={config.required}
        />
      );
    case FormFieldConfigType.DATE:
    case FormFieldConfigType.DATEYM: {
      const mode = config.fieldType === FormFieldConfigType.DATEYM ? "month" : undefined;
      return (
        <DateComponent
          fieldId={config.fieldId}
          fieldName={config.fieldId}
          value={`${currentValue || ""}`}
          onChange={onChange}
          disabled={config.disabled}
          label={<Message id={getMessageIdForLabel(config)} />}
          explanation={
            i18n.exists(getMessageIdForExplanation(config))
              ? msg({ id: getMessageIdForExplanation(config) })
              : undefined
          }
          isErrored={!!errorId}
          errorMsg={errorId && <Message id={`errorId.${errorId}`} />}
          isRequired={config.required}
          mode={mode}
        />
      );
    }
    case FormFieldConfigType.RADIO:
      return (
        <FormFieldRadio
          onChange={onChange}
          value={`${currentValue || ""}`}
          options={getFormFieldSelectOptions(config.fieldId, config.options, msg)}
          required={config.required}
          fieldId={config.fieldId}
          disabled={config.disabled}
          label={<Message id={getMessageIdForLabel(config)} />}
          explanation={
            i18n.exists(getMessageIdForExplanation(config))
              ? msg({ id: getMessageIdForExplanation(config) })
              : undefined
          }
          errorMessage={errorId && <Message id={`errorId.${errorId}`} />}
        />
      );
    default:
      logger.warn(`Unknown field type: ${config.fieldType}`);
      return null;
  }
};
